import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle, Check, Loader2, RefreshCw, ShieldCheck, ShieldQuestion, Trash2, Undo2, X,
} from 'lucide-react';
import { Button } from '../Button';
import { UserProfile } from '../../types';
import {
    ProjectDeletionRequest,
    beschreibeStatus,
    darfAusfuehren,
    darfEntscheiden,
    darfZuruecknehmen,
    istNochOffen,
    sortiereAntraege,
} from '../../services/projectDeletionLogic';
import {
    LOESCHANTRAEGE_GEAENDERT,
    entscheideProjektLoeschung,
    fuehreLoeschungAus,
    ladeLoeschantraege,
    nimmLoeschantragZurueck,
} from '../../services/projectDeletionService';
import { loescheAlleProjektDokumente } from '../../services/projektDokumenteService';

interface Props {
    currentUser: UserProfile | null;
    /** Nach einer ausgefuehrten Loeschung muss die Projektliste neu laden. */
    onProjectsChanged?: () => void;
    onCountChanged?: (offen: number) => void;
}

const datum = (iso?: string | null): string => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
};

/**
 * Die Loeschantraege — Schritt 2 und 3 des Vier-Augen-Prinzips
 * (supabase/project_deletion_approvals.sql, MIGRATIONS.md Nr. 49).
 *
 * Duenner Container: WER hier was darf, beantwortet
 * `src/services/projectDeletionLogic.ts` (ohne DOM und ohne Datenbank
 * pruefbar), und die Datenbank beantwortet es noch einmal — diese Ansicht
 * blendet nur aus, was ohnehin abgewiesen wuerde.
 *
 * Bewusst KEIN „alles freigeben"-Knopf und keine Mehrfachauswahl: eine
 * Freigabe, die man im Dutzend erteilt, ist keine Pruefung.
 */
export const AdminDeletionRequests: React.FC<Props> = ({ currentUser, onProjectsChanged, onCountChanged }) => {
    const [antraege, setAntraege] = useState<ProjectDeletionRequest[]>([]);
    const [laedt, setLaedt] = useState(true);
    const [fehler, setFehler] = useState<string | null>(null);
    const [arbeitetAn, setArbeitetAn] = useState<string | null>(null);
    const [ablehnung, setAblehnung] = useState<{ id: string; notiz: string } | null>(null);
    const [nurOffene, setNurOffene] = useState(true);

    const laden = useCallback(async () => {
        setLaedt(true);
        setFehler(null);
        try {
            const daten = await ladeLoeschantraege(false);
            setAntraege(daten);
            onCountChanged?.(daten.filter((r) => istNochOffen(r)).length);
        } catch (e: any) {
            setFehler(e?.message || 'Die Löschanträge konnten nicht geladen werden.');
        } finally {
            setLaedt(false);
        }
    }, [onCountChanged]);

    useEffect(() => { void laden(); }, [laden]);

    // Im Pflicht-Popup entschieden: die Liste hier soll das sofort zeigen.
    useEffect(() => {
        const neuLaden = () => { void laden(); };
        window.addEventListener(LOESCHANTRAEGE_GEAENDERT, neuLaden);
        return () => window.removeEventListener(LOESCHANTRAEGE_GEAENDERT, neuLaden);
    }, [laden]);

    // Abgelaufene gelten nicht als offen: sie verlangen von niemandem mehr
    // etwas und sperren seit Nr. 159 auch keinen neuen Antrag mehr.
    const sichtbar = useMemo(() => {
        const sortiert = sortiereAntraege(antraege, currentUser);
        return nurOffene ? sortiert.filter((r) => istNochOffen(r)) : sortiert;
    }, [antraege, currentUser, nurOffene]);

    const offeneGesamt = antraege.filter((r) => istNochOffen(r)).length;

    const entscheiden = async (request: ProjectDeletionRequest, freigeben: boolean, notiz?: string) => {
        setArbeitetAn(request.id);
        setFehler(null);
        try {
            await entscheideProjektLoeschung(request.id, freigeben, notiz);
            setAblehnung(null);
            await laden();
        } catch (e: any) {
            setFehler(e?.message || 'Die Entscheidung konnte nicht gespeichert werden.');
        } finally {
            setArbeitetAn(null);
        }
    };

    const zuruecknehmen = async (request: ProjectDeletionRequest) => {
        if (!window.confirm(`Antrag zu "${request.project_name}" zurücknehmen? Das Projekt bleibt bestehen.`)) return;
        setArbeitetAn(request.id);
        setFehler(null);
        try {
            await nimmLoeschantragZurueck(request.id);
            await laden();
        } catch (e: any) {
            setFehler(e?.message || 'Der Antrag konnte nicht zurückgenommen werden.');
        } finally {
            setArbeitetAn(null);
        }
    };

    const ausfuehren = async (request: ProjectDeletionRequest) => {
        // Der letzte Klick der Kette. Er ist unumkehrbar, deshalb steht hier
        // die deutlichste Rueckfrage der ganzen Anwendung — mit Namen, damit
        // niemand die falsche Zeile erwischt.
        const bestaetigt = window.confirm(
            `ENDGÜLTIG LÖSCHEN: "${request.project_name}"${request.project_number ? ` (${request.project_number})` : ''}\n\n`
            + `Freigegeben von: ${request.decided_by_name || '—'}\n`
            + `Begründung: ${request.reason}\n\n`
            + 'Alle Berichte, Mängel, Fotos, Leistungen und Dokumente dieses Projekts werden entfernt. '
            + 'Eine vollständige Sicherung wird vorher angelegt (nur über die Datenbank wiederherstellbar).\n\n'
            + 'Wirklich löschen?',
        );
        if (!bestaetigt) return;

        setArbeitetAn(request.id);
        setFehler(null);
        try {
            const { hinweis } = await fuehreLoeschungAus(
                request.id,
                async () => { await loescheAlleProjektDokumente(request.project_id); },
            );
            await laden();
            onProjectsChanged?.();
            alert(`"${request.project_name}" wurde gelöscht.${hinweis}`);
        } catch (e: any) {
            setFehler(e?.message || 'Das Löschen ist fehlgeschlagen.');
        } finally {
            setArbeitetAn(null);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                        <ShieldQuestion className="w-5 h-5 text-amber-600" /> Löschanträge
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        Vier-Augen-Prinzip: wer löschen will, begründet — ein zufällig ausgeloster
                        zweiter Administrator gibt frei. {offeneGesamt} offen.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 text-xs font-bold text-slate-600">
                        <input
                            type="checkbox"
                            checked={nurOffene}
                            onChange={(e) => setNurOffene(e.target.checked)}
                            className="rounded border-slate-300"
                        />
                        Nur offene
                    </label>
                    <Button variant="secondary" size="sm" onClick={() => void laden()} icon={<RefreshCw className="w-4 h-4" />}>
                        Aktualisieren
                    </Button>
                </div>
            </div>

            {fehler && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 flex gap-2">
                    <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-rose-800 leading-relaxed">{fehler}</p>
                </div>
            )}

            {laedt && (
                <div className="flex items-center gap-2 text-sm text-slate-500 p-6 justify-center">
                    <Loader2 className="w-4 h-4 animate-spin" /> Lädt…
                </div>
            )}

            {!laedt && sichtbar.length === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center">
                    <ShieldCheck className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">
                        {nurOffene ? 'Kein offener Löschantrag.' : 'Noch kein Löschantrag gestellt.'}
                    </p>
                </div>
            )}

            <div className="space-y-3">
                {sichtbar.map((r) => {
                    const status = beschreibeStatus(r);
                    const kannEntscheiden = darfEntscheiden(r, currentUser);
                    const kannAusfuehren = darfAusfuehren(r, currentUser);
                    const kannZuruecknehmen = darfZuruecknehmen(r, currentUser);
                    const beschaeftigt = arbeitetAn === r.id;

                    return (
                        <div
                            key={r.id}
                            className={`rounded-2xl border p-4 bg-white ${
                                kannEntscheiden ? 'border-amber-300 shadow-sm' : kannAusfuehren ? 'border-blue-300 shadow-sm' : 'border-slate-200'
                            }`}
                        >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="font-bold text-slate-800 truncate">
                                        {r.project_number ? <span className="text-slate-400 mr-2">{r.project_number}</span> : null}
                                        {r.project_name}
                                    </p>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        Beantragt von <strong>{r.requested_by_name}</strong> am {datum(r.created_at)} ·
                                        Prüfer <strong>{r.approver_name}</strong>
                                    </p>
                                </div>
                                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border shrink-0 ${status.ton}`}>
                                    {status.text}
                                </span>
                            </div>

                            <p className="mt-3 text-sm text-slate-700 bg-slate-50 rounded-xl p-3 whitespace-pre-wrap break-words">
                                {r.reason}
                            </p>

                            {(r.decided_at || r.executed_at) && (
                                <p className="text-xs text-slate-500 mt-2">
                                    {r.decided_at && <>Entschieden von <strong>{r.decided_by_name || '—'}</strong> am {datum(r.decided_at)}. </>}
                                    {r.decision_note && <>Notiz: {r.decision_note} </>}
                                    {r.executed_at && <>Gelöscht am {datum(r.executed_at)}.</>}
                                </p>
                            )}

                            {ablehnung?.id === r.id ? (
                                <div className="mt-3">
                                    <textarea
                                        autoFocus
                                        rows={2}
                                        value={ablehnung.notiz}
                                        onChange={(e) => setAblehnung({ id: r.id, notiz: e.target.value })}
                                        placeholder="Warum lehnen Sie ab? (geht an den Antragsteller)"
                                        className="w-full rounded-xl border border-slate-200 p-2.5 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100 resize-none"
                                    />
                                    <div className="flex justify-end gap-2 mt-2">
                                        <Button variant="ghost" size="sm" onClick={() => setAblehnung(null)} disabled={beschaeftigt}>
                                            Zurück
                                        </Button>
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            disabled={beschaeftigt}
                                            onClick={() => void entscheiden(r, false, ablehnung.notiz)}
                                            icon={beschaeftigt ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                                        >
                                            Ablehnung senden
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-3 flex flex-wrap justify-end gap-2">
                                    {kannZuruecknehmen && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            disabled={beschaeftigt}
                                            onClick={() => void zuruecknehmen(r)}
                                            icon={<Undo2 className="w-4 h-4" />}
                                        >
                                            Zurückziehen
                                        </Button>
                                    )}
                                    {kannEntscheiden && (
                                        <>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                disabled={beschaeftigt}
                                                onClick={() => setAblehnung({ id: r.id, notiz: '' })}
                                                icon={<X className="w-4 h-4" />}
                                            >
                                                Ablehnen
                                            </Button>
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                disabled={beschaeftigt}
                                                onClick={() => void entscheiden(r, true)}
                                                icon={beschaeftigt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                            >
                                                Freigeben
                                            </Button>
                                        </>
                                    )}
                                    {kannAusfuehren && (
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            disabled={beschaeftigt}
                                            onClick={() => void ausfuehren(r)}
                                            icon={beschaeftigt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                        >
                                            Endgültig löschen
                                        </Button>
                                    )}
                                    {!kannEntscheiden && !kannAusfuehren && r.status === 'pending' && (
                                        <span className="text-xs text-slate-400 self-center">
                                            Wartet auf {r.approver_name}.
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
